import React, { useMemo } from 'react';
import Select, { SingleValue } from 'react-select';
import { Port } from '../../types';
import { useDarkMode } from '../../hooks/useDarkMode';
import { getSelectStyles } from './selectStyles';

interface PortSelectProps {
  ports: Port[];
  value: string;
  onChange: (portId: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

interface PortOption {
  value: string;
  label: string;
}

export const PortSelect: React.FC<PortSelectProps> = ({
  ports,
  value,
  onChange,
  placeholder = 'Select a port',
  disabled = false,
}) => {
  const { isDarkMode } = useDarkMode();

  const options: PortOption[] = useMemo(
    () =>
      ports.map((port) => ({
        value: port.id,
        label: `${port.code} - ${port.port}`,
      })),
    [ports]
  );

  const selectedOption = useMemo(
    () => options.find((opt) => opt.value === value) || null,
    [options, value]
  );

  const customStyles = useMemo(
    () => getSelectStyles<PortOption>(isDarkMode),
    [isDarkMode]
  );

  const handleChange = (newValue: SingleValue<PortOption>) => {
    if (newValue) {
      onChange(newValue.value);
    } else {
      onChange('');
    }
  };

  return (
    <Select<PortOption, false>
      options={options}
      value={selectedOption}
      onChange={handleChange}
      placeholder={placeholder}
      isDisabled={disabled}
      isClearable
      isSearchable
      noOptionsMessage={() => 'No ports found'}
      styles={customStyles}
      className="react-select-container"
      classNamePrefix="react-select"
    />
  );
};
